import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { StatusComponent } from '../../../../../components/ui/status/status.component';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import {
  countryOptions,
  toCountryCode,
} from '../../../../../services/smart-broadcast/smart-broadcast.utils';
import { ISmartBroadcastPhone } from '../../../../../types/smartBroadcast';
import { formatPhoneNumberForDisplay } from '../../../../../../utils/utils';
import { formatCount } from '../../../../../../utils/sms';

interface SampleRow {
  sheetRow: number;
  raw: string;
  sendsTo: string;
  label: string;
  /** Keyword app-status maps to a tone. */
  tone: string;
}

/**
 * Step 2 — which column holds the phone number, which country local numbers
 * belong to, and which rows to leave out. Also lists every column with the
 * field name it goes by in the message.
 */
@Component({
  selector: 'sb-phone-column',
  standalone: true,
  imports: [NgClickOutsideDirective, StatusComponent],
  templateUrl: './phone-column.component.html',
  styleUrls: ['../../sb-shared.css', './phone-column.component.css'],
})
export class PhoneColumnComponent {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  store = inject(SmartBroadcastService);

  @ViewChild('listbox') listbox?: ElementRef<HTMLElement>;

  /** Column-picker listbox state. */
  open = false;
  activeIndex = -1;
  /** Letters typed in quick succession, for typeahead. */
  private typed = '';
  private typedTimer?: ReturnType<typeof setTimeout>;

  readonly countries = countryOptions();
  readonly formatCount = formatCount;

  readonly stats = computed(() => this.store.numberStats());

  readonly countryName = computed(() => {
    const code = this.store.country();
    return this.countries.find((country) => country.code === code)?.name ?? '';
  });

  readonly autoShare = computed(() => {
    const share = this.store.phoneAuto();
    return share === null ? '' : `${Math.round(share * 100)}%`;
  });

  /** The first rows, as written and as they will be sent. */
  readonly sample = computed<SampleRow[]>(() => {
    const phones = this.store.phones();
    const numbers = this.store.rowNumbers();
    return phones.slice(0, 6).map((phone, i) => ({
      sheetRow: numbers[i] ?? i + 2,
      raw: phone.raw,
      sendsTo: this.sendsTo(phone),
      ...this.describe(phone),
    }));
  });

  readonly phoneShortened = computed(() => {
    const column = this.store.columns()[this.store.phoneIndex()];
    return column?.shortened ?? 0;
  });

  get canContinue(): boolean {
    return this.store.phoneIndex() >= 0 && this.stats().send > 0;
  }

  private sendsTo(phone: ISmartBroadcastPhone): string {
    if (phone.state === 'valid') return formatPhoneNumberForDisplay(phone.number);
    return '—';
  }

  private describe(phone: ISmartBroadcastPhone): { label: string; tone: string } {
    const leaveInvalid = this.store.dropInvalid();
    if (phone.state === 'empty') return { label: 'No number, skipped', tone: 'disabled' };
    if (phone.state === 'shortened') return { label: 'Shortened by Excel', tone: 'failed' };
    if (phone.state === 'invalid') {
      return { label: leaveInvalid ? 'Invalid, left out' : 'Invalid, sent as written', tone: 'failed' };
    }
    if (phone.repeatOf >= 0) {
      return { label: this.store.dedupe() ? 'Repeat, left out' : 'Repeat, sent again', tone: 'disabled' };
    }
    return { label: 'Valid', tone: 'sent' };
  }

  display(value: string) {
    return value || '(empty)';
  }

  // ---- column picker ----------------------------------------------------------
  toggle() {
    this.open = !this.open;
    if (this.open) {
      this.activeIndex = this.store.phoneIndex();
      this.scrollActiveIntoView();
    }
  }

  close() {
    this.open = false;
    this.activeIndex = -1;
    this.typed = '';
  }

  choose(index: number) {
    this.store.setPhoneColumn(index);
    this.close();
  }

  /**
   * Keyboard behaviour matching a native select: arrows move, Home/End jump
   * to the ends, typing jumps to the next column starting with those letters,
   * Enter/Space commit.
   */
  onKeydown(event: KeyboardEvent) {
    const { key } = event;
    const count = this.store.headersSig().length;

    if (key === 'Escape' || key === 'Tab') {
      if (this.open && key === 'Escape') event.preventDefault();
      this.close();
      return;
    }

    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      if (!this.open) this.toggle();
      if (!count) return;
      this.activeIndex = key === 'Home' ? 0 : count - 1;
      this.scrollActiveIntoView();
      return;
    }

    // Printable characters search; a space only once a search has started.
    const printable = key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (printable && (key !== ' ' || this.typed)) {
      event.preventDefault();
      if (!this.open) this.toggle();
      this.typeahead(key);
      return;
    }

    if (!this.open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.toggle();
      }
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!count) return;
      const step = key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + count) % count;
      this.scrollActiveIntoView();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (this.activeIndex >= 0) this.choose(this.activeIndex);
    }
  }

  /** Move to the next column whose name starts with what has been typed. */
  private typeahead(char: string) {
    clearTimeout(this.typedTimer);
    this.typedTimer = setTimeout(() => (this.typed = ''), 600);
    // Repeating one letter cycles through the columns starting with it.
    const repeat = this.typed.length === 1 && this.typed === char.toLowerCase();
    this.typed = repeat ? this.typed : this.typed + char.toLowerCase();

    const columns = this.store.columns();
    if (!columns.length) return;
    const from = this.typed.length === 1 ? this.activeIndex + 1 : Math.max(this.activeIndex, 0);
    for (let step = 0; step < columns.length; step++) {
      const i = (from + step) % columns.length;
      const name = (columns[i].header || columns[i].field).toLowerCase();
      if (name.startsWith(this.typed)) {
        this.activeIndex = i;
        this.scrollActiveIntoView();
        return;
      }
    }
  }

  private scrollActiveIntoView() {
    // The list renders (or re-renders) in the same tick the index changes, so wait
    // for layout before measuring the row we want to bring into view.
    requestAnimationFrame(() => {
      const row = this.listbox?.nativeElement.children[this.activeIndex] as HTMLElement | undefined;
      row?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ---- other settings ---------------------------------------------------------
  onCountry(event: Event) {
    this.store.setCountry(toCountryCode((event.target as HTMLSelectElement).value));
  }

  onDropInvalid(event: Event) {
    this.store.dropInvalid.set((event.target as HTMLInputElement).checked);
  }

  onDedupe(event: Event) {
    this.store.dedupe.set((event.target as HTMLInputElement).checked);
  }

  token(field: string): string {
    return `{{${field}}}`;
  }

  renameNote(renamed: string | null): string {
    if (renamed === 'blank') return 'No name in the sheet';
    if (renamed === 'unsafe') return 'Renamed: the sheet’s name has characters a field can’t use';
    if (renamed === 'duplicate') return 'Renamed: another column has the same name';
    return '';
  }

  filledLabel(filled: number): string {
    const total = this.store.rowsSig().length;
    if (!total) return '—';
    if (filled === total) return 'Every row';
    return `${formatCount(filled)} of ${formatCount(total)}`;
  }

  continue() {
    if (this.canContinue) {
      this.next.emit();
    }
  }
}
